import { Button, IconButton, Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FilePlus02, Save01, Trash01 } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { useNotify } from '../hooks';
import { PATHS } from '../paths';
import { AccidentMerchantContent } from './accidents/AccidenMerchantContent';
import { AccidentAddDocumentModal } from './accidents/AccidentAddDocumentModal';
import { AccidentCustomerContent } from './accidents/AccidentCustomerContent';
import { AccidentRemoveConfirmDialog } from './accidents/AccidentRemoveConfirmDialog';
import { parseAccidentDataToRequest } from './accidents/parser';
import { NewCashReceiptDialog, mapAccountingToCashReceipt } from './accountingDocuments/NewCashReceiptDialog';
import { useAccountingDocumets } from './accountingDocuments/hooks/useAccountingDocumets';
import { PageLayout, PageLayoutProps, PageTitle } from './common';
import { useAuth } from './hooks';
import { useLoadAresData } from './hooks/useLoadAresData';
import { useUploadFiles } from './hooks/useUploadFiles';
import { Role } from './types';
import { Accident, AccidentGeneral, AccidentSourcesList, RegistryType } from './types/accident';

interface AccidentPageContentProps extends PageLayoutProps {
  accidentId: string;
  parentPath: string;
}

const AccidentPageContent = ({ accidentId, parentPath, ...props }: AccidentPageContentProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement | null>(null);
  const { notify, notifyOnError, notifyOnSuccess } = useNotify();

  const [openRemoveAccidentDialog, setOpenRemoveAccidentDialog] = useState<number | undefined>(undefined);

  const {
    isCreatingCashReceipt,
    createCashReceipt,
    editingDocument,
    editDocument,
    setEditingDocument,
    setOpenNewDialog,
    openNewDialog,
    handleClose,
    documentCodes,
    accountingCentres,
    isLoadingInvoices,
    accountingDocumentsInvoices
  } = useAccountingDocumets();

  const { data: accident } = useSuspenseQuery(useCommonApi().getAccident(accidentId));
  const { data: acceptanceSources } = useQuery<AccidentSourcesList[]>(
    useCommonApi().getRegistryList(RegistryType.ACCIDENTS_SOURCES)
  );

  const { mutate: updateAccidentData, isPending: isSaving } = useMutation(useCommonApi().updateAccident());
  const { mutate: removeAccident } = useMutation(useCommonApi().removeAccident());

  const { isAresDataLoading, handleLoadAresData, aresData } = useLoadAresData();
  const { handleUploadFiles, isUploadingFiles, openUploadModal, setOpenUploadModal } = useUploadFiles(accident);

  if (accident === null) {
    return null;
  }

  const onUpdateSuccess = onSuccessCustomApiResponse({
    executed: () => {
      queryClient.invalidateQueries({
        queryKey: ['common', 'accidents', 'detail', accidentId]
      });
      notify('success', 'Aktualizace proběhla úspěšně');
    },
    failed: () => notify('error', 'Chyba při aktualizaci')
  });

  const runAccidentUpdate = (patch: Partial<Accident>) => {
    updateAccidentData(
      {
        id: accidentId as string,
        accidentData: parseAccidentDataToRequest(patch as Accident)
      },
      { onSuccess: onUpdateSuccess }
    );
  };

  const handleAccidentUpdate = async (data: Accident) => {
    runAccidentUpdate(data);
  };

  const handleAccidentSourceUpdate = (data: AccidentGeneral) => {
    const source = data?.source;
    if (!source) return null;
    runAccidentUpdate({ source } as Pick<Accident, 'source'>);
  };

  const handleAccidentRemove = () => {
    if (!openRemoveAccidentDialog) return;
    removeAccident(
      { accidentId: openRemoveAccidentDialog?.toString() },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            setOpenRemoveAccidentDialog(undefined);
            notifyOnSuccess('Nehoda byla smazána!');
            navigate(PATHS.ACCIDENTS());
          },
          failed: () => {
            setOpenRemoveAccidentDialog(undefined);
            notifyOnError('Nelze smazat nehodu!');
          }
        })
      }
    );
  };

  const SaveButton = () => (
    <Button
      variant="contained"
      color="success"
      size="large"
      startIcon={<Save01 size="20px" />}
      onClick={() => formRef.current?.requestSubmit()}
      loading={isSaving}
    >
      Uložit
    </Button>
  );

  return (
    <>
      <PageLayout
        title={<PageTitle variant="h2">Detail nehody</PageTitle>}
        backPath={parentPath}
        extra={
          user?.role === Role.Dealer && (
            <Stack direction="row" gap={2} justifyContent="end">
              <Button
                color="info"
                variant="contained"
                startIcon={<FilePlus02 />}
                onClick={() => setOpenNewDialog(true)}
              >
                Doklad
              </Button>
              <SaveButton />
              <IconButton color="error" onClick={() => setOpenRemoveAccidentDialog(Number.parseInt(accidentId))}>
                <Trash01 />
              </IconButton>
            </Stack>
          )
        }
        extraFooter={user?.role === Role.Dealer && <SaveButton />}
        {...props}
      >
        {user?.role === Role.Dealer ? (
          <>
            <AccidentMerchantContent
              accident={accident}
              onModalOpen={(name, limit, title) => setOpenUploadModal({ name, title, limit, accidentId })}
              onSubmit={handleAccidentUpdate}
              formRef={formRef}
              aresData={aresData}
              onAresClick={handleLoadAresData}
              isAresDataLoading={isAresDataLoading}
              acceptanceSources={acceptanceSources}
              onReasonChange={handleAccidentSourceUpdate}
            />
            {/* Add new Dialog */}
            <NewCashReceiptDialog
              open={openNewDialog}
              onClose={handleClose}
              accountingCentres={accountingCentres}
              documentCodes={documentCodes}
              accidentId={Number.parseInt(accidentId)}
              isLoading={isCreatingCashReceipt}
              defaultData={editingDocument ? mapAccountingToCashReceipt(editingDocument) : undefined}
              invoices={accountingDocumentsInvoices}
              invoicesLoading={isLoadingInvoices}
              onSubmit={(formData) => {
                if (editingDocument) {
                  // EDIT
                  editDocument(
                    { documentId: editingDocument.id, formData },
                    {
                      onSuccess: onSuccessCustomApiResponse({
                        executed: () => {
                          setOpenNewDialog(false);
                          setEditingDocument(null);
                        }
                      })
                    }
                  );
                } else {
                  // CREATE
                  createCashReceipt(
                    { formData },
                    {
                      onSuccess: onSuccessCustomApiResponse({
                        executed: () => setOpenNewDialog(false),
                        failed: (_, __, options) => {
                          if (options.statusCode === 439) {
                            setOpenNewDialog(false);
                            notify('success', 'Dokument byl založený, ale nebyl vygenerovaný PDF dokument');
                          }
                        }
                      })
                    }
                  );
                }
              }}
            />
          </>
        ) : (
          <AccidentCustomerContent {...accident} />
        )}
        {/* Add new document Modal */}
        <AccidentAddDocumentModal
          {...openUploadModal}
          onClose={() => setOpenUploadModal(undefined)}
          onUpload={handleUploadFiles}
          isUploading={isUploadingFiles}
        />
        <AccidentRemoveConfirmDialog
          onAccept={handleAccidentRemove}
          open={!!openRemoveAccidentDialog}
          onDeny={() => setOpenRemoveAccidentDialog(undefined)}
          onClose={() => setOpenRemoveAccidentDialog(undefined)}
        />
      </PageLayout>
    </>
  );
};

export const AccidentPage = (props: Omit<AccidentPageContentProps, 'accidentId'>) => {
  const { accidentId } = useParams<{ accidentId: string }>();
  if (!accidentId) return null;
  return <AccidentPageContent accidentId={accidentId} {...props} />;
};
